import { Link } from 'react-router-dom';
import { WHY_CHOOSE_US, COMPANY } from '../../mocks/siteData';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface WhyChooseUsProps {
  className?: string;
}

export default function WhyChooseUs({ className = '' }: WhyChooseUsProps) {
  const { ref, visible } = useScrollAnimation();

  return (
    <section className={`bg-white py-32 px-6 lg:px-16 xl:px-24 ${className}`}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 xl:gap-24 items-center">

          {/* Left: Image composition */}
          <div className="relative">
            {/* Main image */}
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/7310bc9db3f438997c203ac84f4acd29.jpg"
                alt="Bay City Remodeling craftsmanship"
                className="w-full h-[520px] object-cover object-top"
              />
              {/* Dark gradient for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/50 to-transparent" />
            </div>

            {/* Floating review badge — top left */}
            <div className="absolute -top-5 left-8 hidden lg:block bg-white border border-stone-100 rounded-2xl px-6 py-4 z-10">
              <div className="flex items-center gap-1 mb-1.5">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-star-fill text-accent text-xs" />
                  </div>
                ))}
              </div>
              <p className="font-bold text-stone-900 text-sm">{COMPANY.rating} / 5.0</p>
              <p className="text-stone-400 text-xs">{COMPANY.reviewCount} homeowner reviews</p>
            </div>

            {/* Floating years badge — bottom right */}
            <div className="absolute -bottom-5 -right-5 hidden lg:block bg-stone-950 rounded-2xl p-6 z-10">
              <p className="font-serif text-5xl text-accent font-bold leading-none mb-1">{COMPANY.yearsExp}+</p>
              <p className="text-stone-400 text-xs leading-tight">Years of<br />Local Experience</p>
            </div>

            {/* Gold accent line decoration */}
            <div className="absolute top-16 -left-4 hidden lg:block w-px h-28 bg-gradient-to-b from-transparent via-accent to-transparent" />
          </div>

          {/* Right: Content */}
          <div
            ref={ref}
            className={`transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
          >
            <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4">Why Bay City</p>
            <h2
              className="font-serif text-4xl lg:text-[52px] text-stone-900 leading-tight mb-6"
              style={{ letterSpacing: '-0.02em' }}
            >
              Remodeling Without Compromise
            </h2>
            <p className="text-stone-500 text-base leading-relaxed mb-10">
              Homeowners across Brandon, FL and surrounding communities choose us because we deliver exactly what we promise — precision craftsmanship, transparent communication, and results built to last.
            </p>

            <div className="flex flex-col gap-5 mb-10">
              {WHY_CHOOSE_US.map((item, i) => (
                <div key={item.icon} className="flex items-start gap-4 group">
                  <div className="w-11 h-11 rounded-xl bg-stone-50 border border-stone-100 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/10 group-hover:border-accent/20 transition-colors duration-300">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${item.icon} text-stone-400 group-hover:text-accent text-base transition-colors duration-300`} />
                    </div>
                  </div>
                  <div className="pt-1">
                    <h3 className="font-semibold text-stone-900 text-sm mb-1">{item.title}</h3>
                    <p className="text-stone-500 text-sm leading-relaxed">{item.text}</p>
                  </div>
                  {i < WHY_CHOOSE_US.length - 1 && (
                    <div className="absolute left-0 ml-[21px] mt-11 w-px h-5 bg-stone-100 hidden" />
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-3 bg-stone-950 hover:bg-stone-800 text-white font-semibold px-7 py-4 rounded-lg transition-colors whitespace-nowrap group text-sm"
              >
                Request a Design Consultation
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1 text-xs" />
                </div>
              </Link>
              <a
                href={`tel:${COMPANY.phoneFull}`}
                className="inline-flex items-center gap-2 text-stone-500 hover:text-accent transition-colors text-sm font-medium py-4"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-accent text-xs" />
                </div>
                {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
